import { ImageType, PromoImageType } from '@models/Image';
import { SectionStyleSegment } from '@models/PageSection';

type PreviewDef = {
  /**
   * Specifies whether the title should be centered or left aligned. Applies to tile types with title placed below.
   */
  alignText?: 'center' | 'left';
  /**
   * Defines the tile aspect ratio type.
   */
  aspectRatio?: PromoImageType;
  /**
   * Speficiec whether an image should displayed.
   */
  hasImage: boolean;
  /**
   * Specifies whether subtitle should be displayed.
   */
  hasSubtitle: boolean;
  /**
   * Specifies whether title should be displayed.
   */
  hasTitle: boolean;
  /**
   * Specifies which image type should be fetched from image origin (`poster` | `square` | `wide`).
   */
  imageType: ImageType;
  /**
   * Width of image fetched from image origin. Defaults to full size if no value is provided.
   */
  imageWidth?: number;
  /**
   * Specifies the card shape. Most segment types use `box`, but `kids'service` uses `teddy`, and `person` uses `circle`
   */
  shape?: 'box' | 'circle' | 'teddy';
  /**
   * Defines the placement of the title, i.e. whether it should be placed below the tile or overlayed.
   */
  titlePlacement?: 'below' | 'center';
};

export const previewDefs: Record<SectionStyleSegment, PreviewDef> = {
  'banner-fullscreen': {
    aspectRatio: 'wide',
    hasImage: true,
    hasSubtitle: false,
    hasTitle: false,
    imageType: 'wide',
    titlePlacement: 'below',
  },
  'channel-ongoing': {
    aspectRatio: 'wide',
    hasImage: true,
    hasSubtitle: false,
    hasTitle: false,
    imageType: 'wide',
    imageWidth: 300,
  },
  'kids-service': {
    aspectRatio: 'square',
    hasImage: true,
    hasSubtitle: false,
    hasTitle: false,
    imageType: 'square',
    imageWidth: 400,
    shape: 'teddy',
    titlePlacement: 'below',
  },
  'poster-notext': {
    aspectRatio: 'poster',
    hasImage: true,
    hasSubtitle: false,
    hasTitle: false,
    imageType: 'poster',
    imageWidth: 400,
  },
  'promo-wide-large': {
    aspectRatio: 'promo-wide-large',
    hasImage: true,
    hasSubtitle: false,
    hasTitle: false,
    imageType: 'wide',
    imageWidth: 2000,
    titlePlacement: 'below',
  },
  'promo-wide': {
    aspectRatio: 'promo-wide',
    hasImage: true,
    hasSubtitle: false,
    hasTitle: false,
    imageType: 'wide',
    imageWidth: 900,
    titlePlacement: 'below',
  },
  'streaming-service': {
    aspectRatio: 'wide',
    hasImage: true,
    hasSubtitle: false,
    hasTitle: false,
    imageType: 'wide',
    imageWidth: 300,
    titlePlacement: 'below',
  },
  'wide-notext-double': {
    aspectRatio: 'promo-wide',
    imageWidth: 2000,
    hasImage: true,
    hasSubtitle: true,
    hasTitle: true,
    imageType: 'wide',
    titlePlacement: 'below',
  },
  'wide-notext-large': {
    aspectRatio: 'wide',
    hasImage: true,
    hasSubtitle: false,
    hasTitle: false,
    imageType: 'wide',
    imageWidth: 700,
  },
  app: {
    aspectRatio: 'wide',
    hasImage: true,
    hasSubtitle: false,
    hasTitle: false,
    imageType: 'wide',
    imageWidth: 300,
    titlePlacement: 'below',
  },
  channel: {
    aspectRatio: 'wide',
    hasImage: true,
    hasSubtitle: true,
    hasTitle: true,
    imageType: 'wide',
    imageWidth: 300,
    titlePlacement: 'below',
  },
  genre: {
    aspectRatio: 'promo-wide',
    hasImage: false,
    hasSubtitle: false,
    hasTitle: true,
    imageType: 'wide',
    titlePlacement: 'center',
  },
  person: {
    alignText: 'center',
    aspectRatio: 'square',
    hasImage: true,
    hasSubtitle: false,
    hasTitle: true,
    imageType: 'square',
    imageWidth: 400,
    shape: 'circle',
    titlePlacement: 'below',
  },
  poster: {
    alignText: 'center',
    aspectRatio: 'poster',
    hasImage: true,
    hasSubtitle: false,
    hasTitle: true,
    imageType: 'poster',
    imageWidth: 400,
    titlePlacement: 'below',
  },
  wide: {
    aspectRatio: 'wide',
    hasImage: true,
    hasSubtitle: true,
    hasTitle: true,
    imageType: 'wide',
    imageWidth: 400,
    titlePlacement: 'below',
  },
  'wide-airtime': {
    aspectRatio: 'wide',
    hasImage: true,
    hasSubtitle: true,
    hasTitle: true,
    imageType: 'wide',
    imageWidth: 400,
    titlePlacement: 'below',
  },
};

export default previewDefs;
